import React, { useContext, useMemo, useState, useCallback, useEffect } from 'react';
import useWorkforceTelemetry from '../../hooks/useWorkforceTelemetry';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import WorkspacePage from '../../components/layout/WorkspacePage';
import {
  EmployeeWorkspaceLayout
} from '../../components/workforce/EmployeeWorkspace';
import Card from '../../components/ui/Card';
import Pill from '../../components/ui/Pill';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import StatusBanner from '../../components/ui/StatusBanner';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useEmployeeProfile } from '../../hooks/useEmployeeProfile';
import { useEmployeeProfileMutations } from '../../hooks/useEmployeeProfileMutations';
import { useAccessibleLocations } from '../../hooks/useAccessibleLocations';
import { formatRoleLabel } from '../../config/appRoutes';
import {
  AddressModal,
  EditEmployeeProfileModal,
  EmergencyContactModal,
  PreferencesPanelEditor
} from './EmployeeProfileModals';
import EmployeePhotoUploadModal from './EmployeePhotoUploadModal';
import EmployeeDocumentsPanel from './EmployeeDocumentsPanel';
import EmployeeAuditTab from './EmployeeAuditTab';
import EmployeeCoachingTab from './EmployeeCoachingTab';
import EmployeeAccessTab from './EmployeeAccessTab';
import EmployeeOverviewDashboard from './EmployeeOverviewDashboard';
import EmployeePerformanceWorkspace from './EmployeePerformanceWorkspace';
import EmployeeCareerWorkspace from './EmployeeCareerWorkspace';
import EmployeeTrainingCertificationsWorkspace from './EmployeeTrainingCertificationsWorkspace';
import EmployeeOperationalKpisWorkspace from './EmployeeOperationalKpisWorkspace';
import EmployeeAiInsightsWorkspace from './EmployeeAiInsightsWorkspace';
import EmployeeGoalsWorkspace from './EmployeeGoalsWorkspace';
import EmployeeOrganizationWorkspace from './EmployeeOrganizationWorkspace';
import EmployeeWorkforcePlanningWorkspace from './EmployeeWorkforcePlanningWorkspace';
import InlineEditableField from '../../components/ui/InlineEditableField';
import OperationalRequestWorkspace from '../../components/workspace/OperationalRequestWorkspace';
import { buildRequestQueueSummary, filterRequestsByQueue } from '../../components/requests/requestUtils';
import { apiRequest } from '../../services/apiClient';
// W5: read auth via useContext directly (not useAuth) so existing
// tests rendering EmployeeProfilePage outside an AuthProvider keep
// working — useAuth throws when no provider is mounted, which would
// regress callers that never needed the user object. Outside a
// provider we default to "no manage_users grant", which collapses
// to the W4 tab set.
import { AuthContext } from '../../context/AuthContext';
import { useTenantModules } from '../../context/TenantConfigContext';
import './EmployeeProfilePage.css';

const EMPLOYMENT_TYPE_LABELS = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contract: 'Contract',
  seasonal: 'Seasonal',
  intern: 'Intern'
};

const RELOCATION_LABELS = {
  no: 'Not open to relocation',
  local: 'Open to local relocation',
  regional: 'Open to regional relocation',
  national: 'Open to national relocation',
  international: 'Open to international relocation'
};

const ADDRESS_TYPE_LABELS = {
  home: 'Home',
  mailing: 'Mailing',
  prior_home: 'Prior home',
  other: 'Other'
};

const ATTENDANCE_EVENT_TYPE_LABELS = {
  sick_call: 'Sick call',
  other_absence: 'Absence',
  late_arrival: 'Late arrival'
};

const DIRECTOR_ATTENDANCE_FOCUS_STORAGE_KEY = 'director-attendance-focus-manager';

function isPendingTimeOffStatus(status) {
  const normalized = String(status || '').toLowerCase();
  return normalized === 'pending' || normalized === 'review needed';
}

function isApprovedTimeOffStatus(status) {
  return String(status || '').toLowerCase() === 'approved';
}

function isDeniedTimeOffStatus(status) {
  return String(status || '').toLowerCase() === 'denied';
}

function isUpcomingEvent(event, todayKey) {
  return String(event?.endDate || event?.startDate || '') >= todayKey;
}

function isClosedTimeOffEvent(event, todayKey) {
  return isDeniedTimeOffStatus(event?.status)
    || (isApprovedTimeOffStatus(event?.status) && String(event?.endDate || event?.startDate || '') < todayKey);
}

function computeInitials(bundle, fallbackManagerId) {
  const profile = bundle?.profile;
  if (profile?.preferredName) {
    return profile.preferredName.trim().slice(0, 2).toUpperCase();
  }
  if (fallbackManagerId) {
    return `#${fallbackManagerId}`.slice(0, 3);
  }
  return '··';
}

function formatDate(value) {
  if (!value) return null;
  const stringified = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(stringified)) return stringified;
  return stringified;
}

function resolveProfileDisplayName(profile, fallbackManagerId) {
  return profile?.preferredName
    || [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim()
    || (fallbackManagerId ? `Employee workspace #${fallbackManagerId}` : `Employee #${profile?.id || '—'}`);
}

function formatPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return `${numeric.toFixed(1)}%`;
}

function toSentenceCase(value) {
  if (!value) return '';
  const normalized = String(value).trim();
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function getAttendanceTone(level) {
  if (level === 'Critical') return 'danger';
  if (level === 'Concern') return 'warning';
  if (level === 'Watch') return 'info';
  return 'neutral';
}

function Row({ label, value }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <>
      <dt>{label}</dt>
      <dd className={empty ? 'employee-profile__rows-dd--empty' : ''}>
        {empty ? '—' : value}
      </dd>
    </>
  );
}

/**
 * W3: row variant that mounts an InlineEditableField in its <dd>.
 * The field auto-falls back to a read-only display when readOnly=true,
 * so callers can pass `readOnly={!canEdit}` to gate by permission
 * without changing the markup.
 */
function EditableRow({
  label,
  value,
  type = 'text',
  readOnly,
  onSave,
  telemetrySubtype,
  'data-testid': testId
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        <InlineEditableField
          value={value}
          type={type}
          label={label}
          readOnly={readOnly}
          onSave={onSave}
          telemetrySubtype={telemetrySubtype}
          data-testid={testId}
        />
      </dd>
    </>
  );
}

function SectionCard({ title, children, action = null }) {
  return (
    <Card className="employee-profile__section" padding="md" shadow="sm">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

function IdentitySection({ profile, canEditProfile = false, onInlineSave = null }) {
  // W3: only `preferredName` is inline-editable in this first
  // migration. Other identity fields (pronouns, birth date, languages)
  // continue to render read-only here — the full Edit Profile modal
  // remains the mechanism for those edits during W3.
  const inlineSaveAvailable = canEditProfile && typeof onInlineSave === 'function';
  return (
    <SectionCard title="Identity">
      <dl className="employee-profile__rows">
        <EditableRow
          label="Preferred name"
          value={profile.preferredName}
          type="text"
          readOnly={!inlineSaveAvailable}
          onSave={(next) => onInlineSave('preferredName', next)}
          telemetrySubtype="inline_preferred_name"
          data-testid="inline-edit-preferred-name"
        />
        <Row label="Pronouns" value={profile.pronouns} />
        <Row label="Birth date" value={formatDate(profile.birthDate)} />
        <Row
          label="Languages"
          value={Array.isArray(profile.languagesSpoken) && profile.languagesSpoken.length
            ? profile.languagesSpoken.join(', ')
            : ''}
        />
      </dl>
    </SectionCard>
  );
}

function ContactSection({ profile, action = null, canEditContact = false, onInlineSave = null }) {
  // W3: personalEmail + personalPhone become inline-editable. The
  // existing "Edit contact" modal trigger (passed in via `action`)
  // remains so multi-field edits keep their fallback path.
  const inlineSaveAvailable = canEditContact && typeof onInlineSave === 'function';
  return (
    <SectionCard title="Contact" action={action}>
      <dl className="employee-profile__rows">
        <EditableRow
          label="Personal email"
          value={profile.personalEmail}
          type="email"
          readOnly={!inlineSaveAvailable}
          onSave={(next) => onInlineSave('personalEmail', next)}
          telemetrySubtype="inline_personal_email"
          data-testid="inline-edit-personal-email"
        />
        <EditableRow
          label="Personal phone"
          value={profile.personalPhone}
          type="tel"
          readOnly={!inlineSaveAvailable}
          onSave={(next) => onInlineSave('personalPhone', next)}
          telemetrySubtype="inline_personal_phone"
          data-testid="inline-edit-personal-phone"
        />
      </dl>
    </SectionCard>
  );
}

// Workforce People Model Phase 2 — controlled select for the
// employment_classification field. The classification CHECK constraint
// (Phase 1) accepts only 'hourly' or 'salary' (plus NULL), so the
// select must be limited to those exact values. A blank option clears
// the field by sending null through onSave (matching the schema's
// nullable contract).
const EMPLOYMENT_CLASSIFICATION_LABELS = Object.freeze({
  hourly: 'Hourly',
  salary: 'Salary'
});
const EMPLOYMENT_CLASSIFICATION_OPTIONS = Object.freeze([
  { value: '',       label: 'Not set' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'salary', label: 'Salary' }
]);

function ClassificationSelectRow({ label, value, readOnly, onSave, 'data-testid': testId }) {
  // The native <select> is the right primitive here: it's a
  // 3-option controlled list, accessible by default, and the value
  // is one of a small fixed enum. We don't reuse InlineEditableField
  // because that primitive is purpose-built for free-text inputs.
  const [draft, setDraft] = React.useState(value || '');
  React.useEffect(() => {
    setDraft(value || '');
  }, [value]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleChange = async (event) => {
    const next = event.target.value;
    setDraft(next);
    if ((next || '') === (value || '')) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      // Send null to clear the field; the schema rejects '' as an
      // enum value but accepts null and undefined.
      await onSave(next === '' ? null : next);
    } catch (err) {
      setError(err?.message || 'Save failed.');
      // Roll back the visible draft so the operator sees the
      // previous value.
      setDraft(value || '');
    } finally {
      setBusy(false);
    }
  };

  if (readOnly) {
    return (
      <>
        <dt>{label}</dt>
        <dd data-testid={testId}>
          {value ? (EMPLOYMENT_CLASSIFICATION_LABELS[value] || value) : ''}
        </dd>
      </>
    );
  }

  return (
    <>
      <dt>{label}</dt>
      <dd>
        <select
          value={draft}
          onChange={handleChange}
          disabled={busy}
          aria-label={label}
          data-testid={testId}
        >
          {EMPLOYMENT_CLASSIFICATION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {busy ? (
          <span className="inline-edit__status" data-testid={testId ? `${testId}-saving` : undefined} aria-live="polite">
            Saving…
          </span>
        ) : null}
        {error ? (
          <span className="inline-edit__error" data-testid={testId ? `${testId}-error` : undefined} role="alert">
            {error}
          </span>
        ) : null}
      </dd>
    </>
  );
}

function EmploymentSection({ profile, supervisorLabel = 'Unassigned', canEditProfile = false, onInlineSave = null }) {
  // Workforce People Model Phase 2: surface the three classification
  // fields (jobTitle, department, employmentClassification) added in
  // Phase 1. Other employment fields (hire date, employment type,
  // work eligibility, bio, career aspirations, relocation) remain
  // read-only here — the Edit Profile modal is still the mechanism
  // for those. `managers.manager_role_key` and the leadership roster
  // are NOT touched.
  const inlineSaveAvailable = canEditProfile && typeof onInlineSave === 'function';
  return (
    <SectionCard title="Employment">
      <dl className="employee-profile__rows">
        {/* People Model Phase 4A — operator-facing Employee ID is
            inline-editable. The service-side validator normalizes
            whitespace, enforces per-company uniqueness, and rejects
            bare blank strings — so the existing EditableRow
            primitive is sufficient and the server returns a 409/400
            on conflict that the inline editor surfaces. */}
        <EditableRow
          label="Employee ID"
          value={profile.employeeNumber}
          type="text"
          readOnly={!inlineSaveAvailable}
          onSave={(next) => onInlineSave('employeeNumber', next)}
          telemetrySubtype="inline_employee_number"
          data-testid="inline-edit-employee-number"
        />
        <EditableRow
          label="Job title"
          value={profile.jobTitle}
          type="text"
          readOnly={!inlineSaveAvailable}
          onSave={(next) => onInlineSave('jobTitle', next)}
          telemetrySubtype="inline_job_title"
          data-testid="inline-edit-job-title"
        />
        <EditableRow
          label="Department"
          value={profile.department}
          type="text"
          readOnly={!inlineSaveAvailable}
          onSave={(next) => onInlineSave('department', next)}
          telemetrySubtype="inline_department"
          data-testid="inline-edit-department"
        />
        <ClassificationSelectRow
          label="Employment classification"
          value={profile.employmentClassification}
          readOnly={!inlineSaveAvailable}
          onSave={(next) => onInlineSave('employmentClassification', next)}
          data-testid="inline-edit-employment-classification"
        />
        <Row label="Hire date" value={formatDate(profile.dateOfHire)} />
        <Row
          label="Employment type"
          value={profile.employmentType ? EMPLOYMENT_TYPE_LABELS[profile.employmentType] || profile.employmentType : ''}
        />
        <Row label="Reports to / Supervisor" value={supervisorLabel || 'Unassigned'} />
        <Row label="Work eligibility" value={profile.workEligibilityStatus} />
        <Row label="Bio" value={profile.bio} />
        <Row label="Career aspirations" value={profile.careerAspirations} />
        <Row
          label="Relocation"
          value={profile.relocationWillingness ? RELOCATION_LABELS[profile.relocationWillingness] || profile.relocationWillingness : ''}
        />
      </dl>
    </SectionCard>
  );
}

function AddressList({ addresses, isArchived, canWritePII, canReadPII, piiRedacted, onAdd, onEdit, onSetPrimary, onDelete }) {
  if (piiRedacted) {
    return (
      <SectionCard title="Addresses">
        <EmptyState
          compact
          icon="·"
          headline="Restricted"
          body="Addresses are visible only to users with the appropriate permission."
        />
      </SectionCard>
    );
  }
  const disabled = isArchived || !canWritePII;
  const addAction = canWritePII || isArchived ? (
    <Button size="sm" variant="secondary" onClick={onAdd} disabled={disabled}>Add address</Button>
  ) : null;
  if (!Array.isArray(addresses) || addresses.length === 0) {
    return (
      <SectionCard title="Addresses" action={addAction}>
        <EmptyState
          compact
          icon="·"
          headline="No addresses on file"
          body="Addresses appear here once they have been added to this profile."
        />
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Addresses" action={addAction}>
      <div className="employee-profile__list">
        {addresses.map((address) => (
          <div key={address.id} className="employee-profile__list-item">
            <div className="employee-profile__list-item-title">
              <span>{ADDRESS_TYPE_LABELS[address.addressType] || address.addressType}</span>
              {address.isPrimary ? <Pill variant="primary" fullText>Primary</Pill> : null}
            </div>
            <div className="employee-profile__list-item-meta">
              <div>{address.line1}{address.line2 ? `, ${address.line2}` : ''}</div>
              <div>
                {[address.city, address.stateRegion, address.postalCode].filter(Boolean).join(', ')}
                {address.countryCode ? ` · ${address.countryCode}` : ''}
              </div>
            </div>
            {canWritePII ? (
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onEdit(address)}
                  disabled={disabled}
                  data-testid={`address-edit-${address.id}`}
                >
                  Edit
                </Button>
                {!address.isPrimary ? (
                  <Button size="sm" variant="secondary" onClick={() => onSetPrimary(address)} disabled={disabled}>
                    Make primary
                  </Button>
                ) : null}
                <Button size="sm" variant="secondary" onClick={() => onDelete(address)} disabled={disabled}>Delete</Button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function EmergencyContactList({ contacts, isArchived, canWritePII, piiRedacted, onAdd, onEdit, onDelete }) {
  if (piiRedacted) {
    return (
      <SectionCard title="Emergency contacts">
        <EmptyState
          compact
          icon="·"
          headline="Restricted"
          body="Emergency contacts are visible only to users with the appropriate permission."
        />
      </SectionCard>
    );
  }
  const disabled = isArchived || !canWritePII;
  const addAction = canWritePII || isArchived ? (
    <Button size="sm" variant="secondary" onClick={onAdd} disabled={disabled}>Add contact</Button>
  ) : null;
  if (!Array.isArray(contacts) || contacts.length === 0) {
    return (
      <SectionCard title="Emergency contacts" action={addAction}>
        <EmptyState
          compact
          icon="·"
          headline="No emergency contacts on file"
          body="Add an emergency contact to support after-hours coverage decisions."
        />
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Emergency contacts" action={addAction}>
      <div className="employee-profile__list">
        {contacts.map((contact) => (
          <div key={contact.id} className="employee-profile__list-item">
            <div className="employee-profile__list-item-title">
              <span>{contact.fullName}</span>
              {contact.relationship ? <Pill variant="neutral" fullText>{contact.relationship}</Pill> : null}
            </div>
            <div className="employee-profile__list-item-meta">
              {[contact.phonePrimary, contact.phoneAlt, contact.email].filter(Boolean).join(' · ') || '—'}
            </div>
            {canWritePII ? (
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <Button size="sm" variant="secondary" onClick={() => onEdit(contact)} disabled={disabled}>Edit</Button>
                <Button size="sm" variant="secondary" onClick={() => onDelete(contact)} disabled={disabled}>Delete</Button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function PreferencesPanel({ preferences, isArchived, canWrite, isEditing, onEdit, onCancelEdit, onSave }) {
  const editAction = canWrite ? (
    <Button size="sm" variant="secondary" onClick={onEdit} disabled={isArchived || isEditing || !canWrite}>
      {preferences ? 'Edit' : 'Set up'}
    </Button>
  ) : null;
  if (isEditing) {
    return (
      <SectionCard title="Preferences">
        <PreferencesPanelEditor preferences={preferences} onSave={onSave} onCancel={onCancelEdit} />
      </SectionCard>
    );
  }
  if (!preferences) {
    return (
      <SectionCard title="Preferences" action={editAction}>
        <EmptyState
          compact
          icon="·"
          headline="Defaults in use"
          body="Personalized notification, timezone, and language preferences appear here once set."
        />
      </SectionCard>
    );
  }
  const channels = [
    preferences.notificationChannelEmail ? 'Email' : null,
    preferences.notificationChannelSms ? 'SMS' : null,
    preferences.notificationChannelPush ? 'Push' : null
  ].filter(Boolean).join(', ');
  const quiet = preferences.notificationQuietHoursStart && preferences.notificationQuietHoursEnd
    ? `${preferences.notificationQuietHoursStart} – ${preferences.notificationQuietHoursEnd}`
    : '';
  return (
    <SectionCard title="Preferences" action={editAction}>
      <dl className="employee-profile__rows">
        <Row label="Notify on" value={channels} />
        <Row label="Quiet hours" value={quiet} />
        <Row label="Timezone" value={preferences.timezone} />
        <Row label="Language" value={preferences.displayLanguage ? preferences.displayLanguage.toUpperCase() : ''} />
      </dl>
    </SectionCard>
  );
}

function AttendanceMetricCard({ label, value, meta = null, tone = 'neutral' }) {
  return (
    <Card className={`employee-attendance__metric employee-attendance__metric--${tone}`} padding="md" shadow="sm">
      <div className="employee-attendance__metric-label">{label}</div>
      <div className="employee-attendance__metric-value">{value}</div>
      {meta ? <div className="employee-attendance__metric-meta">{meta}</div> : null}
    </Card>
  );
}

function AttendanceSummarySection({ row }) {
  const windows = [
    { label: '30 days', sick: row.sick30, absence: row.absence30, late: row.late30, total: row.totalIncidents30 },
    { label: '60 days', sick: row.sick60, absence: row.absence60, late: row.late60, total: row.totalIncidents60 },
    { label: '90 days', sick: row.sick90, absence: row.absence90, late: row.late90, total: row.totalIncidents90 }
  ];

  return (
    <>
      <div className="employee-attendance__metrics" data-testid="employee-attendance-summary">
        <AttendanceMetricCard
          label="Current risk"
          value={row.alertLevel || 'Normal'}
          meta={row.disciplineReviewFlag ? toSentenceCase(row.disciplineReviewFlag) : 'No discipline review flag'}
          tone={getAttendanceTone(row.alertLevel)}
        />
        <AttendanceMetricCard
          label="At-work %"
          value={formatPercent(row.atWorkRate)}
          meta={row.trend ? `Trend: ${row.trend}` : 'Trend unavailable'}
          tone="success"
        />
        {windows.map((window) => (
          <AttendanceMetricCard
            key={window.label}
            label={window.label}
            value={String(window.total ?? 0)}
            meta={`Sick ${window.sick ?? 0} · Absence ${window.absence ?? 0} · Late ${window.late ?? 0}`}
            tone="neutral"
          />
        ))}
      </div>
      <SectionCard title="Attendance summary">
        <dl className="employee-profile__rows">
          <Row label="Alert level" value={row.alertLevel || 'Normal'} />
          <Row label="At-work %" value={formatPercent(row.atWorkRate)} />
          <Row label="Trend" value={row.trend || ''} />
          <Row label="Discipline review" value={toSentenceCase(row.disciplineReviewFlag) || ''} />
          <Row label="Location" value={row.locationCode || ''} />
          <Row label="Manager-backed" value={row.isManagerBacked ? 'Yes' : 'No'} />
        </dl>
      </SectionCard>
    </>
  );
}

function AttendanceHistorySection({ historyStatus, historyItems, historyError, historyUnsupported }) {
  if (historyStatus === 'loading') {
    return (
      <SectionCard title="Recent attendance history">
        <LoadingSkeleton variant="paragraph" lines={4} />
      </SectionCard>
    );
  }

  if (historyUnsupported) {
    return (
      <SectionCard title="Recent attendance history">
        <EmptyState
          compact
          icon="·"
          headline="Recent history not available yet"
          body="This person can still be tracked in attendance risk. Recent event history will appear here once the person-scoped history endpoint is available."
        />
      </SectionCard>
    );
  }

  if (historyError) {
    return (
      <SectionCard title="Recent attendance history">
        <StatusBanner kind="error" message={historyError} />
      </SectionCard>
    );
  }

  if (!historyItems.length) {
    return (
      <SectionCard title="Recent attendance history">
        <EmptyState
          compact
          icon="·"
          headline="No recent attendance events"
          body="Attendance events such as sick calls, absences, and late arrivals will appear here when they are recorded."
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Recent attendance history">
      <div className="employee-profile__list" data-testid="employee-attendance-history">
        {historyItems.map((event) => (
          <div key={event.id} className="employee-profile__list-item">
            <div className="employee-profile__list-item-title">
              <span>{ATTENDANCE_EVENT_TYPE_LABELS[event.eventType] || event.eventType}</span>
              <Pill variant={event.status === 'recorded' ? 'info' : 'neutral'} fullText>
                {toSentenceCase(event.status) || 'Recorded'}
              </Pill>
            </div>
            <div className="employee-profile__list-item-meta">
              <div>
                {[event.startDate, event.endDate && event.endDate !== event.startDate ? `to ${event.endDate}` : null]
                  .filter(Boolean)
                  .join(' ')}
              </div>
              <div>
                {[event.timeBlock, event.locationCode, event.reason].filter(Boolean).join(' · ') || 'No additional detail'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function AttendanceTab({ profile, effectiveManagerId }) {
  const profileId = Number(profile?.id);
  const [riskState, setRiskState] = useState({ status: 'loading', row: null, error: '' });
  const [historyState, setHistoryState] = useState({
    status: 'idle',
    items: [],
    error: '',
    unsupported: false
  });

  useEffect(() => {
    let cancelled = false;
    setRiskState({ status: 'loading', row: null, error: '' });
    setHistoryState({
      status: 'loading',
      items: [],
      error: '',
      unsupported: false
    });
    apiRequest(`/api/employees/profiles/${profileId}/attendance`)
      .then((data) => {
        if (cancelled) return;
        const events = Array.isArray(data?.events) ? data.events : [];
        setRiskState({ status: 'loaded', row: data?.summary || null, error: '' });
        setHistoryState({
          status: 'loaded',
          items: events.slice(0, 6),
          error: '',
          unsupported: false
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setRiskState({
          status: 'error',
          row: null,
          error: err?.message || 'Unable to load attendance summary.'
        });
        setHistoryState({
          status: 'error',
          items: [],
          error: err?.message || 'Unable to load recent attendance history.',
          unsupported: false
        });
      });
    return () => { cancelled = true; };
  }, [profileId]);

  if (riskState.status === 'loading') {
    return (
      <div className="employee-hub__column employee-hub__column--wide">
        <Card padding="md" shadow="sm">
          <LoadingSkeleton variant="paragraph" lines={6} />
        </Card>
      </div>
    );
  }

  if (riskState.status === 'error') {
    return (
      <div className="employee-hub__column employee-hub__column--wide">
        <StatusBanner kind="error" message={riskState.error || 'Unable to load attendance summary.'} />
      </div>
    );
  }

  const hasAttendanceData = Boolean(riskState.row) || historyState.items.length > 0;

  if (!hasAttendanceData) {
    return (
      <div className="employee-hub__column employee-hub__column--wide">
        <EmptyState
          icon="·"
          headline="No attendance data yet"
          body="Attendance summary and recent attendance events will appear here after attendance activity has been recorded for this person."
        />
        <AttendanceHistorySection
          historyStatus={historyState.status}
          historyItems={historyState.items}
          historyError={historyState.error}
          historyUnsupported={historyState.unsupported}
        />
      </div>
    );
  }

  return (
    <div className="employee-hub__column employee-hub__column--wide">
      {riskState.row ? <AttendanceSummarySection row={riskState.row} /> : null}
      <AttendanceHistorySection
        historyStatus={historyState.status}
        historyItems={historyState.items}
        historyError={historyState.error}
        historyUnsupported={historyState.unsupported}
      />
    </div>
  );
}

function TimeOffSummaryCard({ label, value, meta = null, tone = 'neutral' }) {
  return (
    <Card className={`employee-timeoff__metric employee-timeoff__metric--${tone}`} padding="md" shadow="sm">
      <div className="employee-timeoff__metric-label">{label}</div>
      <div className="employee-timeoff__metric-value">{value}</div>
      {meta ? <div className="employee-timeoff__metric-meta">{meta}</div> : null}
    </Card>
  );
}

function TimeOffLinkRow() {
  return (
    <div className="employee-timeoff__links" data-testid="employee-timeoff-links">
      <a className="employee-profile__related-link" href="/requests-calendar">
        Open requests workspace
      </a>
      <a className="employee-profile__related-link" href="/requests-calendar#shared-time-off-calendar">
        Open shared calendar
      </a>
    </div>
  );
}

function TimeOffTab({ profile, effectiveManagerId }) {
  const profileId = Number(profile?.id);
  const [requestsState, setRequestsState] = useState({ status: 'idle', items: [], error: '' });
  const [requestFilter, setRequestFilter] = useState('all');
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const todayKey = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    let cancelled = false;
    setRequestsState({ status: 'loading', items: [], error: '' });
    apiRequest(`/api/employees/profiles/${profileId}/time-off`)
      .then((data) => {
        if (cancelled) return;
        const items = (Array.isArray(data?.requests) ? data.requests : [])
          .sort((left, right) => {
            const leftKey = `${left.startDate || ''}:${left.id || 0}`;
            const rightKey = `${right.startDate || ''}:${right.id || 0}`;
            return rightKey.localeCompare(leftKey);
          })
          .map((event) => ({
            ...event,
            managerName: event.displayName || event.managerName || resolveProfileDisplayName(profile, effectiveManagerId)
          }));
        setRequestsState({ status: 'loaded', items, error: '' });
      })
      .catch((err) => {
        if (cancelled) return;
        setRequestsState({
          status: 'error',
          items: [],
          error: err?.message || 'Unable to load time-off requests.'
        });
      });
    return () => { cancelled = true; };
  }, [effectiveManagerId, profile, profileId]);

  const requestSummary = useMemo(
    () => buildRequestQueueSummary(requestsState.items),
    [requestsState.items]
  );

  const filteredRequests = useMemo(
    () => filterRequestsByQueue(requestsState.items, requestFilter),
    [requestFilter, requestsState.items]
  );

  useEffect(() => {
    if (!filteredRequests.length) {
      setSelectedRequestId('');
      return;
    }
    if (!filteredRequests.some((request) => String(request.id) === String(selectedRequestId))) {
      setSelectedRequestId(String(filteredRequests[0].id));
    }
  }, [filteredRequests, selectedRequestId]);

  const selectedRequest = useMemo(
    () => requestsState.items.find((request) => String(request.id) === String(selectedRequestId)) || null,
    [requestsState.items, selectedRequestId]
  );

  const pendingCount = useMemo(
    () => requestsState.items.filter((event) => isPendingTimeOffStatus(event.status)).length,
    [requestsState.items]
  );
  const approvedUpcomingCount = useMemo(
    () => requestsState.items.filter((event) => isApprovedTimeOffStatus(event.status) && isUpcomingEvent(event, todayKey)).length,
    [requestsState.items, todayKey]
  );
  const deniedOrClosedCount = useMemo(
    () => requestsState.items.filter((event) => isClosedTimeOffEvent(event, todayKey)).length,
    [requestsState.items, todayKey]
  );
  const latestUpcomingRequest = useMemo(
    () => requestsState.items.find((event) => isApprovedTimeOffStatus(event.status) && isUpcomingEvent(event, todayKey)) || null,
    [requestsState.items, todayKey]
  );

  if (requestsState.status === 'loading') {
    return (
      <div className="employee-hub__column employee-hub__column--wide">
        <Card padding="md" shadow="sm">
          <LoadingSkeleton variant="paragraph" lines={6} />
        </Card>
      </div>
    );
  }

  if (requestsState.status === 'error') {
    return (
      <div className="employee-hub__column employee-hub__column--wide">
        <StatusBanner kind="error" message={requestsState.error || 'Unable to load time-off requests.'} />
      </div>
    );
  }

  return (
    <div className="employee-hub__column employee-hub__column--wide">
      <div className="employee-timeoff__metrics" data-testid="employee-timeoff-summary">
        <TimeOffSummaryCard
          label="Pending requests"
          value={String(pendingCount)}
          meta={requestSummary.reviewNeeded ? `${requestSummary.reviewNeeded} need policy review` : 'Awaiting decision'}
          tone={pendingCount > 0 ? 'warning' : 'neutral'}
        />
        <TimeOffSummaryCard
          label="Approved upcoming"
          value={String(approvedUpcomingCount)}
          meta={latestUpcomingRequest ? `${latestUpcomingRequest.startDate} to ${latestUpcomingRequest.endDate}` : 'No upcoming approved time off'}
          tone={approvedUpcomingCount > 0 ? 'success' : 'neutral'}
        />
        <TimeOffSummaryCard
          label="Denied / closed"
          value={String(deniedOrClosedCount)}
          meta="Denied requests and completed approved time off"
          tone={deniedOrClosedCount > 0 ? 'info' : 'neutral'}
        />
        <TimeOffSummaryCard
          label="Total requests"
          value={String(requestsState.items.length)}
          meta={requestsState.items.length ? 'Visible from existing manager-backed request history' : 'No requests on file'}
          tone="neutral"
        />
      </div>

      <TimeOffLinkRow />

      <OperationalRequestWorkspace
        sectionTitle="Time-off requests"
        countLabel="shown"
        introCopy="Review this person's request statuses, approved upcoming time off, and decision history without leaving the Person Workspace."
        requestFilter={requestFilter}
        requestSummary={requestSummary}
        includeAll
        queueAllLabel="All Time-Off Requests"
        filteredRequests={filteredRequests}
        isInitialLoading={false}
        emptyMessage="No time-off requests found for this profile."
        noResultsColSpan={5}
        selectedRequestId={selectedRequestId}
        skeletonColumnCount={5}
        tableClassName="ops-requests-table"
        detailRequest={selectedRequest}
        detailRequestTitle="Selected Time-Off Request"
        detailEmptyMessage="Select a time-off request above to view the latest decision details and history."
        detailHistoryTitle="Time-Off Decision History"
        onSelectQueue={setRequestFilter}
        onSelectRequest={setSelectedRequestId}
      />
    </div>
  );
}

function CompletenessIndicator({ score }) {
  const safeScore = Math.max(0, Math.min(100, Number(score) || 0));
  return (
    <div className="employee-profile__completeness" aria-label={`Profile ${safeScore} percent complete`}>
      <div className="employee-profile__completeness-score">{safeScore}%</div>
      <div className="employee-profile__completeness-label">Profile complete</div>
      <div className="employee-profile__completeness-bar" aria-hidden>
        <div
          className="employee-profile__completeness-bar-fill"
          style={{ width: `${safeScore}%` }}
        />
      </div>
    </div>
  );
}

function ProfileHeader({ bundle, managerId, initials, photoUrl, canManagePhoto, onUploadPhoto }) {
  const profile = bundle?.profile || {};
  const isArchived = Boolean(profile.archivedAt);
  const photoBtnDisabled = isArchived || !canManagePhoto;
  const hasManagerRecord = Boolean(profile.managerId || managerId);
  return (
    <header className="employee-profile__header">
      <button
        type="button"
        className="employee-profile__avatar-button"
        onClick={onUploadPhoto}
        disabled={photoBtnDisabled}
        aria-label={photoUrl ? 'Change employee photo' : 'Upload employee photo'}
        title={
          !canManagePhoto
            ? 'Photo management requires the employees.photo.manage permission'
            : (isArchived ? 'Restore the profile to upload a photo' : 'Upload employee photo')
        }
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            className="employee-profile__avatar-img"
            data-testid="employee-profile-avatar-img"
          />
        ) : (
          <div className="employee-profile__avatar" aria-hidden>{initials}</div>
        )}
      </button>
      <div className="employee-profile__identity">
        <h1>
          {resolveProfileDisplayName(profile, managerId)}
        </h1>
        <div className="employee-profile__identity-meta">
          {/* People Model Phase 4A — operator-facing Employee ID.
              Surfaced FIRST so operators see the human-readable
              identifier before the internal Manager # reference. */}
          {profile.employeeNumber ? (
            <span data-testid="employee-profile-employee-id">
              Employee ID: <strong>{profile.employeeNumber}</strong>
            </span>
          ) : null}
          {hasManagerRecord ? <span>Roster record #{profile.managerId || managerId}</span> : null}
          {profile.employmentType ? (
            <Pill variant="neutral" fullText>{EMPLOYMENT_TYPE_LABELS[profile.employmentType] || profile.employmentType}</Pill>
          ) : null}
          {isArchived ? <Pill variant="warning" fullText>Archived</Pill> : null}
        </div>
      </div>
      <CompletenessIndicator score={profile.profileCompletenessScore} />
    </header>
  );
}

function ProfileActionRow({ isArchived, isOffboarded, busy, canWrite, canOffboard, onEditProfile, onOffboard, onRestore }) {
  if (!canWrite) return null;
  return (
    <div className="employee-profile__cta-row" aria-label="Profile actions">
      <Button size="sm" variant="primary" onClick={onEditProfile} disabled={isArchived || busy}>
        Edit profile
      </Button>
      {isOffboarded ? null : isArchived ? (
        <Button size="sm" variant="secondary" onClick={onRestore} disabled={busy}>Restore profile</Button>
      ) : canOffboard ? (
        <Button size="sm" variant="secondary" onClick={onOffboard} disabled={busy}>Offboard Employee</Button>
      ) : null}
    </div>
  );
}

function PersonWorkspaceActionStrip({
  canManageAccess,
  canWrite,
  hasActiveLinkedAccess,
  isArchived,
  onAddPto,
  onDeactivateAccess,
  onEditProfile,
  onLogAttendance,
  onManageAccess
}) {
  const workflowDisabled = !canWrite || isArchived;
  return (
    <section
      className="employee-profile__action-hub"
      aria-label="Person workspace actions"
      data-testid="employee-profile-action-strip"
    >
      <div className="employee-profile__action-hub-copy">
        <strong>Action Hub</strong>
        <span>Launch the most common profile, attendance, time-off, and access workflows from this person workspace.</span>
      </div>
      <div className="employee-profile__action-hub-actions">
        <Button
          size="sm"
          variant="primary"
          onClick={onEditProfile}
          disabled={workflowDisabled}
          data-testid="person-action-edit-profile"
        >
          Edit Profile
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onLogAttendance}
          disabled={workflowDisabled}
          data-testid="person-action-log-attendance"
        >
          Log Attendance
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onAddPto}
          disabled={workflowDisabled}
          data-testid="person-action-add-pto"
        >
          Add PTO
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onManageAccess}
          disabled={!canManageAccess}
          data-testid="person-action-manage-access"
        >
          Manage Access
        </Button>
        {hasActiveLinkedAccess ? (
          <Button
            size="sm"
            variant="danger"
            onClick={onDeactivateAccess}
            data-testid="person-action-deactivate-access"
          >
            Deactivate Access
          </Button>
        ) : null}
      </div>
    </section>
  );
}

/**
 * People & Talent Hub — tabbed workspace navigation.
 *
 * Replaces the single 2-column "everything visible at once" layout
 * with five clearly-scoped tabs so each surface gets a distinct
 * visual identity instead of looking like a duplicate.
 *
 * Tab state lives in the URL (?tab=…) so deep links survive
 * refreshes and so legacy /employees/profile/:managerId paths
 * without a tab param default to the Overview tab cleanly.
 */
// W2: Overview + Employee Profile merged into a single Profile tab.
// Certifications promoted to a first-class tab (no longer hidden
// inside Documents). Audit promoted to a first-class read-only tab.
// `?tab=overview` deep links continue to resolve via TAB_ALIASES so
// legacy bookmarks keep landing on the right panel.
// W5 promotes Coaching + Access from standalone-only surfaces into
// first-class Hub tabs. Both are permission-gated and are only added
// to the visible tab set when the caller has the matching permission
// (see resolveVisibleHubTabs). Default order keeps the new tabs near
// the lifecycle tabs (audit / learning) so the most-used tabs stay
// leftmost. The standalone /users page remains available — the
// Access tab links into it for actions.
const ALL_HUB_TABS = [
  { id: 'profile',        label: 'Overview' },
  { id: 'performance',    label: 'Performance' },
  { id: 'goals',          label: 'Goals' },
  { id: 'organization',   label: 'Organization & Team' },
  { id: 'workforce-planning', label: 'Workforce Planning' },
  { id: 'coaching',       label: 'Coaching',  requiresCoachingVisibility: true },
  { id: 'career',         label: 'Career' },
  { id: 'documents',      label: 'Documents' },
  { id: 'learning',       label: 'Training & Certifications' },
  { id: 'certifications', label: 'Certifications' },
  { id: 'attendance',     label: 'Attendance' },
  { id: 'time-off',       label: 'Time Off' },
  { id: 'operational-kpis', label: 'Operational KPIs' },
  { id: 'ai-insights',    label: 'AI Insights' },
  { id: 'access',         label: 'Permissions', requiresAccessVisibility: true },
  { id: 'audit',          label: 'History' },
  { id: 'contact',        label: 'Contact Info' },
];

// Permission gate: a single shared visibility resolver so the tab bar
// AND the panel renderer agree on which tabs are mounted. Returning
// just-the-ids keeps the comparison cheap in HUB_TABS.some() calls.
function resolveVisibleHubTabs({ canViewCoaching, canViewAccess, isModuleEnabled }) {
  return ALL_HUB_TABS.filter(tab => {
    if (tab.requiresCoachingVisibility && !canViewCoaching) return false;
    if (tab.requiresAccessVisibility && !canViewAccess) return false;
    if (tab.id === 'attendance' && !isModuleEnabled('attendance')) return false;
    if (tab.id === 'time-off' && !isModuleEnabled('requests')) return false;
    if ((tab.id === 'documents' || tab.id === 'certifications') && !isModuleEnabled('documents')) return false;
    if (tab.id === 'coaching' && !isModuleEnabled('manager_notes')) return false;
    if (tab.id === 'learning' && !isModuleEnabled('learning')) return false;
    return true;
  });
}
const TAB_ALIASES = Object.freeze({
  // Legacy deep links land on the merged Profile tab.
  overview: 'profile',
  training: 'learning',
  permissions: 'access',
  history: 'audit'
});

function HubTabBar({ activeTab, onChange, tabs }) {
  return (
    <nav className="employee-hub__tabs" aria-label="Employee Workspace sections" role="tablist">
      {tabs.map(tab => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`employee-hub__tab${isActive ? ' employee-hub__tab--active' : ''}`}
            onClick={() => onChange(tab.id)}
            data-testid={`hub-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}

/**
 * People & Talent UX Consolidation — person-centered deep links.
 *
 * Operators traditionally bounced between /managers, /users, and
 * the profile page to manage the same employee. This row surfaces
 * the most common adjacent admin destinations directly from the
 * profile so a single search lands on every related surface.
 *
 * Links are anchors (not router Links) because Leadership Directory /
 * Users & Access live inside App.js's conditional render tree,
 * which uses its own internal navigation. Standard <a href> hands
 * off to AppRouter and lets each shell handle the route the way
 * it always has.
 */
function PeopleTalentLinks({ profile, managerId }) {
  const linkTarget = profile?.managerId || managerId;
  return (
    <div className="employee-profile__related" aria-label="Related workforce surfaces" data-testid="employee-profile-related-links">
      <span className="employee-profile__related-label">Related:</span>
      <a className="employee-profile__related-link" href="/managers" data-testid="related-manager-roster">
        Leadership Directory
      </a>
      <a className="employee-profile__related-link" href="/users" data-testid="related-users-access">
        Users & Access
      </a>
      {linkTarget ? (
        <a
          className="employee-profile__related-link"
          href={`/workforce?q=${encodeURIComponent(`manager-${linkTarget}`)}`}
          data-testid="related-directory"
        >
          Back to People directory
        </a>
      ) : null}
    </div>
  );
}

function HubStatusCard({ title, status, body, tone = 'neutral', actions = [] }) {
  return (
    <Card className="employee-hub-card" padding="md" shadow="sm">
      <div className="employee-hub-card__header">
        <h2>{title}</h2>
        <Pill variant={tone} fullText>{status}</Pill>
      </div>
      <p>{body}</p>
      {actions.length ? (
        <div className="employee-hub-card__actions">
          {actions.map(action => (
            action.href ? (
              <a
                key={action.label}
                className="ui-button ui-button--sm ui-button--secondary"
                href={action.href}
                data-testid={action.testId}
              >
                {action.label}
              </a>
            ) : (
              <Button
                key={action.label}
                size="sm"
                variant={action.variant || 'secondary'}
                onClick={action.onClick}
                disabled={action.disabled}
                data-testid={action.testId}
              >
                {action.label}
              </Button>
            )
          ))}
        </div>
      ) : null}
    </Card>
  );
}

function EmployeeHubCards({
  profile,
  profileComplete,
  isArchived,
  perms,
  manager,
  linkedUser,
  canonicalUserLink = null,
  documents,
  onCompleteProfile,
  onUploadDocument,
  onAddCertification,
  onDeactivateAccess
}) {
  const userLinked = Boolean(canonicalUserLink?.id);
  const role = linkedUser?.role || manager?.managerRoleKey || null;
  const locationSummary = [
    manager?.locationCode || null,
    ...((linkedUser?.assignedLocations || []).map(location => location.locationCode || null))
  ].filter(Boolean);
  const activeDocuments = (documents || []).filter(doc => !doc.archivedAt);
  const certificationDocs = activeDocuments.filter(doc => doc.documentType === 'certification');
  const expiringCerts = certificationDocs.filter(doc => {
    if (!doc.expiresAt) return false;
    const expiresMs = new Date(doc.expiresAt).getTime();
    if (Number.isNaN(expiresMs)) return false;
    return expiresMs <= Date.now() + 30 * 24 * 60 * 60 * 1000;
  });
  const canWriteProfile = perms.canWrite && !isArchived;
  const canWriteDocs = perms.canWriteDocuments && !isArchived;
  return (
    <section className="employee-hub-grid" aria-label="Employee hub status cards" data-testid="employee-profile-hub-cards">
      <HubStatusCard
        title="Profile"
        status={profileComplete ? 'Complete' : 'Needs detail'}
        tone={profileComplete ? 'success' : 'warning'}
        body={profileComplete
          ? 'Core identity and employment details are in good shape.'
          : 'Complete identity, contact, and employment details from this profile.'}
        actions={[
          { label: 'Complete profile', onClick: onCompleteProfile, disabled: !canWriteProfile, testId: 'hub-complete-profile' }
        ]}
      />
      <HubStatusCard
        title="Operational Role"
        status={role ? formatRoleLabel(role) : 'Unassigned'}
        tone={role ? 'info' : 'warning'}
        body={manager
          ? `Roster record #${profile?.managerId || manager.id} anchors staffing, coverage, goals, actuals, and operational history.`
          : 'No operational roster record is attached yet.'}
        actions={[
          { label: 'Open roster record', href: '/managers', testId: 'hub-open-roster' }
        ]}
      />
      <HubStatusCard
        title="User Access"
        status={userLinked ? 'Linked' : 'Not linked'}
        tone={userLinked ? 'success' : 'warning'}
        body={userLinked
          ? 'This person has a linked user account. Manage permissions or reset access from Users & Access.'
          : 'No user account is linked yet. Create or link access when this person needs to sign in.'}
        actions={userLinked ? [
          { label: 'Manage permissions', href: '/users', testId: 'hub-manage-permissions' },
          { label: 'Reset password', href: '/users', testId: 'hub-reset-password' },
          { label: linkedUser?.isActive === false ? 'Access inactive' : 'Deactivate access', onClick: onDeactivateAccess, disabled: !linkedUser?.id || linkedUser?.isActive === false, testId: 'hub-deactivate-access' }
        ] : [
          { label: 'Create/link user access', href: '/users', testId: 'hub-create-user-access' }
        ]}
      />
      <HubStatusCard
        title="Documents"
        status={activeDocuments.length ? `${activeDocuments.length} on file` : 'Missing docs'}
        tone={activeDocuments.length ? 'success' : 'warning'}
        body={activeDocuments.length
          ? 'Documents can be opened, updated, and archived from this profile.'
          : 'No active documents are on file yet.'}
        actions={[
          { label: 'Upload document', onClick: onUploadDocument, disabled: !canWriteDocs, testId: 'hub-upload-document' }
        ]}
      />
      <HubStatusCard
        title="Certifications"
        status={expiringCerts.length ? `${expiringCerts.length} expiring` : (certificationDocs.length ? 'Current' : 'Missing certs')}
        tone={expiringCerts.length ? 'danger' : (certificationDocs.length ? 'success' : 'warning')}
        body={certificationDocs.length
          ? 'Certification documents are tracked with expiration pills and the Expiring Certifications workspace.'
          : 'Add certification documents here so expirations can be tracked.'}
        actions={[
          { label: 'Add certification', onClick: onAddCertification, disabled: !canWriteDocs, testId: 'hub-add-certification' },
          { label: 'View expiring', href: '/workforce?focus=expiring', testId: 'hub-view-expiring' }
        ]}
      />
      <HubStatusCard
        title="Locations"
        status={locationSummary.length ? `${Array.from(new Set(locationSummary)).length} assigned` : 'No location'}
        tone={locationSummary.length ? 'success' : 'warning'}
        body={locationSummary.length
          ? `Visible scope: ${Array.from(new Set(locationSummary)).join(', ')}.`
          : 'Assign a roster location or access location before this person can operate in a location context.'}
        actions={[
          { label: 'Manage locations', href: '/users', testId: 'hub-manage-locations' }
        ]}
      />
    </section>
  );
}

function LoadingState() {
  return (
    <div className="employee-profile" data-testid="employee-profile-loading">
      <header className="employee-profile__header">
        <LoadingSkeleton variant="circle" circleSize={64} />
        <div style={{ flex: 1 }}>
          <LoadingSkeleton variant="line" width="40%" height={20} />
          <div style={{ height: 8 }} />
          <LoadingSkeleton variant="line" width="25%" height={12} />
        </div>
        <LoadingSkeleton variant="line" width={140} height={12} />
      </header>
      <div className="employee-profile__grid">
        <Card padding="md" shadow="sm"><LoadingSkeleton variant="paragraph" lines={6} /></Card>
        <Card padding="md" shadow="sm"><LoadingSkeleton variant="paragraph" lines={6} /></Card>
      </div>
    </div>
  );
}

/**
 * Alex Angulo linkage remediation — Phase D.
 *
 * Manager exists but no employee_profiles row points at it. The
 * previous behavior was a blank page; this state replaces that
 * blank with explicit operator messaging including the manager's
 * full name (when known via the managerOverlay fetch) and the
 * canonical manager id.
 *
 * Profile creation is operator-driven — we never auto-create.
 */
function NoProfileState({
  managerId,
  managerName = null,
  managerLocationCode = null,
  onBack,
  onCreateProfile,
  isCreating = false,
  error = '',
  embedded = false
}) {
  const displayName = (managerName || '').trim();
  const locationSuffix = managerLocationCode ? ` · ${managerLocationCode}` : '';
  const subjectLabel = displayName
    ? `${displayName} (Roster record #${managerId}${locationSuffix})`
    : `Roster record #${managerId}${locationSuffix}`;
  return (
    <div
      className={embedded ? 'employee-profile employee-profile--embedded' : 'employee-profile'}
      data-testid="employee-profile-no-profile-state"
    >
      {!embedded ? (
        <div className="employee-profile__topbar">
          <button type="button" className="employee-profile__back" onClick={onBack}>← Back</button>
        </div>
      ) : null}
      {error ? <StatusBanner kind="error" message={error} live /> : null}
      <EmptyState
        icon="·"
        headline="No employee profile exists yet."
        body={`${subjectLabel} has no employee profile on file. Create one to start tracking identity, contact, employment classification, and emergency information.`}
        primaryCtaLabel={isCreating ? 'Creating…' : 'Create profile'}
        onPrimaryCta={onCreateProfile}
      />
    </div>
  );
}

function ErrorState({ error, onRetry, onBack }) {
  return (
    <div className="employee-profile">
      <div className="employee-profile__topbar">
        <button type="button" className="employee-profile__back" onClick={onBack}>← Back</button>
      </div>
      <StatusBanner
        kind="error"
        message={error || 'Unable to load the employee profile.'}
        onRetry={onRetry}
        retryLabel="Retry"
        live
      />
    </div>
  );
}

export default function EmployeeProfilePage({
  embedded = false,
  managerIdOverride = null,
  profileIdOverride = null,
  onNavigate = null,
  onProfileMutated = null
}) {
  const {
    managerId: managerIdParam,
    employeeProfileId: employeeProfileIdParam
  } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const profileId = Number(profileIdOverride ?? employeeProfileIdParam);
  const managerId = Number(managerIdOverride ?? managerIdParam);
  const profileIdValid = Number.isFinite(profileId) && profileId > 0;
  const managerIdValid = Number.isFinite(managerId) && managerId > 0;
  const hasProfileSelectionInput = profileIdOverride != null || employeeProfileIdParam != null;
  const usesDirectProfileSelection = profileIdValid && !managerIdValid;

  // People & Talent Hub — current tab. Defaults to 'overview' so
  // legacy deep links without a ?tab param land on a coherent
  // summary view instead of the densest sub-page.
  // W2: default tab is the merged 'profile'. Legacy '?tab=overview'
  // deep links resolve via TAB_ALIASES so old bookmarks keep working.
  // W5: permission-gated visibility for the Coaching + Access tabs.
  // `manage_users` is the same gate that protects the standalone
  // /users page and the embedded CoachingLog write affordances — so
  // every user who can see those today can also see these tabs.
  // Server-side reads remain tenant-scoped by req.user.companyId, so
  // even if the gate were misconfigured the API cannot leak rows
  // across tenants.
  const authContext = useContext(AuthContext);
  const { isModuleEnabled } = useTenantModules();
  const authUser = authContext?.user || null;
  const userPermissions = Array.isArray(authUser?.permissions) ? authUser.permissions : [];
  const canManageUsersPerm = userPermissions.includes('manage_users');
  const canViewCoaching = canManageUsersPerm;
  const canViewAccess = canManageUsersPerm;

  const rawTab = (searchParams.get('tab') || 'profile').toLowerCase();
  const aliasedTab = TAB_ALIASES[rawTab] || rawTab;
  const { recordEvent } = useWorkforceTelemetry();
  const handleTabChange = useCallback((nextTab) => {
    const params = new URLSearchParams(searchParams);
    // W2: 'profile' is the new default — omit the ?tab param for it
    // so the canonical URL stays clean. Other tabs persist their id.
    if (!nextTab || nextTab === 'profile') {
      params.delete('tab');
    } else {
      params.set('tab', nextTab);
    }
    setSearchParams(params, { replace: false });
    recordEvent({ eventType: 'tab.open', eventSubtype: nextTab || 'profile' });
  }, [searchParams, setSearchParams, recordEvent]);

  const { bundle, status, error, refetch } = useEmployeeProfile(
    profileIdValid ? { profileId, managerId: managerIdValid ? managerId : null } : (managerIdValid ? { managerId } : null)
  );
  const hasManagerRecord = Boolean(bundle?.profile?.managerId || managerIdValid);
  const visibleHubTabs = useMemo(
    () => resolveVisibleHubTabs({ canViewCoaching, canViewAccess, isModuleEnabled }),
    [canViewCoaching, canViewAccess, isModuleEnabled]
  );
  const activeTab = visibleHubTabs.some(t => t.id === aliasedTab) ? aliasedTab : 'profile';
  const initials = useMemo(() => computeInitials(bundle, managerId), [bundle, managerId]);

  // Mutation orchestration. The page owns the optimistic refetch so
  // the bundle, completeness score, and audit timeline stay coherent
  // after every write — the mutation hook exposes raw API mutators
  // rather than per-action state, by design.
  const loadedProfileId = bundle?.profile?.id || null;
  const mutations = useEmployeeProfileMutations({ profileId: loadedProfileId });
  const { locations: accessibleLocations, isLoading: accessibleLocationsLoading } = useAccessibleLocations();
  const confirm = useConfirm();
  const [activeModal, setActiveModal] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [isEditingPreferences, setIsEditingPreferences] = useState(false);
  const [actionBanner, setActionBanner] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [documentUploadRequest, setDocumentUploadRequest] = useState(0);
  const [documentUploadType, setDocumentUploadType] = useState('other');
  const [managerOverlay, setManagerOverlay] = useState(null);
  const [linkedUserOverlay, setLinkedUserOverlay] = useState(null);
  const [documentOverlay, setDocumentOverlay] = useState([]);
  const [editSupervisorOptions, setEditSupervisorOptions] = useState([]);
  const [isCreatingMissingProfile, setIsCreatingMissingProfile] = useState(false);
  const [createMissingProfileError, setCreateMissingProfileError] = useState('');
  const effectiveManagerId = bundle?.profile?.managerId || managerId || null;
  const currentSupervisorLabel = useMemo(() => {
    const supervisorId = bundle?.profile?.supervisorEmployeeId;
    if (!(Number(supervisorId) > 0)) {
      return 'Unassigned';
    }
    const match = editSupervisorOptions.find((option) => Number(option.id) === Number(supervisorId));
    return match?.label || `Employee #${supervisorId}`;
  }, [bundle?.profile?.supervisorEmployeeId, editSupervisorOptions]);

  // Resolve a viewable URL for the primary photo. The signed-url
  // endpoint returns either an S3 presigned URL (s3 backend) or a
  // same-origin /object path (local backend) — either way the
  // frontend opaque-consumes it via <img src>.
  const primaryPhotoId = bundle?.primaryPhoto?.id;
  useEffect(() => {
    if (!primaryPhotoId) {
      setPhotoUrl(null);
      return undefined;
    }
    let cancelled = false;
    apiRequest(`/api/employees/photos/${primaryPhotoId}/signed-url`)
      .then((data) => {
        if (!cancelled && data?.url) setPhotoUrl(data.url);
      })
      .catch(() => {
        if (!cancelled) setPhotoUrl(null);
      });
    return () => { cancelled = true; };
  }, [primaryPhotoId]);

  useEffect(() => {
    if (!hasManagerRecord || !effectiveManagerId) {
      setManagerOverlay(null);
      return undefined;
    }
    let cancelled = false;
    apiRequest(`/api/managers/${effectiveManagerId}`)
      .then(data => { if (!cancelled) setManagerOverlay(data || null); })
      .catch(() => { if (!cancelled) setManagerOverlay(null); });
    return () => { cancelled = true; };
  }, [effectiveManagerId, hasManagerRecord]);

  useEffect(() => {
    const userId = bundle?.canonicalUserLink?.id;
    if (!userId) {
      setLinkedUserOverlay(null);
      return undefined;
    }
    let cancelled = false;
    apiRequest(`/api/users/${userId}`)
      .then(data => { if (!cancelled) setLinkedUserOverlay(data || null); })
      .catch(() => { if (!cancelled) setLinkedUserOverlay(null); });
    return () => { cancelled = true; };
  }, [bundle?.canonicalUserLink?.id]);

  useEffect(() => {
    let cancelled = false;
    apiRequest('/api/employees/profiles?archived=false&limit=100')
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        const options = items
          .filter((item) => Number(item.id) !== Number(loadedProfileId))
          .map((item) => {
            const displayName = item.preferredName
              || [item.firstName, item.lastName].filter(Boolean).join(' ').trim()
              || [item.managerFirstName, item.managerLastName].filter(Boolean).join(' ').trim()
              || `Employee #${item.id}`;
            const locationCode = item.managerLocationCode || '';
            return {
              id: item.id,
              label: locationCode ? `${displayName} (${locationCode})` : displayName
            };
          });
        setEditSupervisorOptions(options);
      })
      .catch(() => {
        if (!cancelled) setEditSupervisorOptions([]);
      });
    return () => { cancelled = true; };
  }, [loadedProfileId]);

  const fetchDocumentOverlay = useCallback(async () => {
    if (!loadedProfileId || !bundle?.permissions?.canReadDocuments) {
      return [];
    }
    try {
      const archiveQuery = (activeTab === 'career' || activeTab === 'learning') ? '?includeArchived=true' : '';
      const data = await apiRequest(`/api/employees/profiles/${loadedProfileId}/documents${archiveQuery}`);
      return Array.isArray(data?.documents) ? data.documents : null;
    } catch (_err) {
      return [];
    }
  }, [activeTab, loadedProfileId, bundle?.permissions?.canReadDocuments]);

  const refreshDocumentOverlay = useCallback(async () => {
    const nextDocuments = await fetchDocumentOverlay();
    if (Array.isArray(nextDocuments)) {
      setDocumentOverlay(nextDocuments);
      return nextDocuments;
    }
    return documentOverlay;
  }, [documentOverlay, fetchDocumentOverlay]);

  useEffect(() => {
    let cancelled = false;
    fetchDocumentOverlay().then((nextDocuments) => {
      if (cancelled || !Array.isArray(nextDocuments)) return;
      setDocumentOverlay(nextDocuments);
    });
    return () => { cancelled = true; };
  }, [fetchDocumentOverlay]);

  // Telemetry: map the loose `activeModal` token (which is one of
  // 'profile' | 'address' | 'contact') to the canonical telemetry
  // subtype, and use the editing-* state to distinguish "add" from
  // "edit" for address/contact. Returns null for un-tracked tokens.
  const modalTelemetrySubtype = useCallback((modal) => {
    if (modal === 'profile') return 'edit_profile';
    if (modal === 'address') return editingAddress ? 'edit_address' : 'add_address';
    if (modal === 'contact') return editingContact ? 'edit_contact' : 'add_contact';
    return null;
  }, [editingAddress, editingContact]);

  // W4: subtypes that present as a drawer (not a center modal). When
  // open/close fire for one of these, we also emit the drawer.*
  // counterpart so the W4 migration window can be measured directly.
  const DRAWER_PRESENTED_SUBTYPES = React.useMemo(
    () => new Set(['add_address', 'edit_address', 'add_contact', 'edit_contact']),
    []
  );

  const openModal = useCallback((modal) => {
    setActiveModal(modal);
    const subtype = modalTelemetrySubtype(modal);
    if (subtype) {
      recordEvent({ eventType: 'modal.open', eventSubtype: subtype });
      if (DRAWER_PRESENTED_SUBTYPES.has(subtype)) {
        recordEvent({ eventType: 'drawer.open', eventSubtype: subtype });
      }
    }
  }, [modalTelemetrySubtype, recordEvent, DRAWER_PRESENTED_SUBTYPES]);

  const closeAllModals = useCallback((options = {}) => {
    const reason = options.reason || 'cancel';
    const subtype = modalTelemetrySubtype(activeModal);
    if (subtype) {
      recordEvent({ eventType: 'modal.close', eventSubtype: subtype, properties: { reason } });
      if (DRAWER_PRESENTED_SUBTYPES.has(subtype)) {
        recordEvent({ eventType: 'drawer.close', eventSubtype: subtype, properties: { reason } });
      }
      if (reason === 'cancel' || reason === 'esc' || reason === 'backdrop') {
        recordEvent({ eventType: 'edit.abandon', eventSubtype: subtype, properties: { reason } });
      }
    }
    setActiveModal(null);
    setEditingAddress(null);
    setEditingContact(null);
  }, [activeModal, modalTelemetrySubtype, recordEvent, DRAWER_PRESENTED_SUBTYPES]);

  const handleMutation = useCallback(async (label, fn) => {
    setActionBusy(true);
    setActionError('');
    try {
      const result = await fn();
      setActionBanner(label);
      await refetch();
      await onProfileMutated?.();
      return result;
    } catch (err) {
      setActionError(err?.message || 'Action failed.');
      throw err;
    } finally {
      setActionBusy(false);
    }
  }, [onProfileMutated, refetch]);

  // Telemetry wrapper: emit edit.commit on a successful save, then
  // mark the next closeAllModals as 'save' so it doesn't also fire
  // edit.abandon. The flag is per-callback; modal close races are
  // benign because the worst case is a missed event, never a
  // workflow break.
  const commitWithTelemetry = useCallback(async (subtype, runner) => {
    const started = Date.now();
    const result = await runner();
    const latencyMs = Date.now() - started;
    recordEvent({ eventType: 'edit.commit', eventSubtype: subtype, properties: { latencyMs } });
    // The modal's onClose will fire after this resolves; pre-empt the
    // abandon-on-cancel branch by closing here with reason='save'.
    closeAllModals({ reason: 'save' });
    return result;
  }, [recordEvent, closeAllModals]);

  const handleEditProfileSave = useCallback((payload) => (
    commitWithTelemetry('edit_profile', () => handleMutation('Profile updated.', () => mutations.updateProfile(payload)))
  ), [commitWithTelemetry, handleMutation, mutations]);

  /**
   * W3: single-field inline save. The InlineEditableField primitive
   * calls this with a (fieldName, newValue) tuple. We dispatch to the
   * same updateProfile mutation the Edit Profile modal uses, so the
   * server contract is unchanged. Telemetry for inline edits is
   * emitted BY the primitive itself (edit.start / edit.commit /
   * edit.abandon / edit.fail), so we deliberately bypass
   * commitWithTelemetry here to avoid double-counting events.
   * Errors are rethrown so the primitive can roll back its UI.
   */
  const handleInlineProfileSave = useCallback(async (fieldName, nextValue) => {
    await handleMutation('Profile updated.', () => mutations.updateProfile({
      [fieldName]: nextValue
    }));
  }, [handleMutation, mutations]);

  const handleAddressSave = useCallback((payload) => {
    const subtype = editingAddress ? 'edit_address' : 'add_address';
    if (editingAddress) {
      return commitWithTelemetry(subtype, () => handleMutation('Address updated.', () => mutations.updateAddress(editingAddress.id, payload)));
    }
    return commitWithTelemetry(subtype, () => handleMutation('Address added.', () => mutations.createAddress(payload)));
  }, [commitWithTelemetry, handleMutation, mutations, editingAddress]);

  const handleContactSave = useCallback((payload) => {
    const subtype = editingContact ? 'edit_contact' : 'add_contact';
    if (editingContact) {
      return commitWithTelemetry(subtype, () => handleMutation('Emergency contact updated.', () => mutations.updateEmergencyContact(editingContact.id, payload)));
    }
    return commitWithTelemetry(subtype, () => handleMutation('Emergency contact added.', () => mutations.createEmergencyContact(payload)));
  }, [commitWithTelemetry, handleMutation, mutations, editingContact]);

  const handlePreferencesSave = useCallback((payload) => (
    handleMutation('Preferences saved.', () => mutations.upsertPreferences(payload))
  ), [handleMutation, mutations]);

  const handleOffboard = useCallback(async () => {
    const ok = await confirm(
      'Offboard this employee? This deactivates the employee, removes linked login access, revokes active sessions, disables kiosk/PIN access, and preserves historical records.',
      { confirmLabel: 'Offboard Employee', tone: 'warning' }
    );
    if (!ok) return;
    const reason = window.prompt('Reason for offboarding (required):', '');
    if (reason == null) return;
    if (reason.trim().length < 3) {
      window.alert('Enter a reason of at least 3 characters.');
      return;
    }
    handleMutation('Employee offboarded. Historical records were preserved.', () => mutations.offboardProfile(reason.trim())).catch(() => {});
  }, [confirm, handleMutation, mutations]);

  const handleRestore = useCallback(async () => {
    handleMutation('Profile restored.', () => mutations.restoreProfile()).catch(() => {});
  }, [handleMutation, mutations]);

  const handleDeactivateAccess = useCallback(async () => {
    if (!linkedUserOverlay?.id || linkedUserOverlay.isActive === false) return;
    handleMutation('User access deactivated.', async () => {
      const updated = await apiRequest(`/api/users/${linkedUserOverlay.id}/access`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: false })
      });
      setLinkedUserOverlay(updated);
      return updated;
    }).catch(() => {});
  }, [handleMutation, linkedUserOverlay]);

  const handleSetPrimaryAddress = useCallback((address) => {
    // Optimistic: flip is_primary in the visible list immediately.
    // The refetch in handleMutation reconciles with the server.
    handleMutation('Primary address updated.', () => mutations.setPrimaryAddress(address.id)).catch(() => {});
  }, [handleMutation, mutations]);

  const handleDeleteAddress = useCallback(async (address) => {
    const ok = await confirm(
      `Remove this address? ${address.line1}, ${address.city}`,
      { confirmLabel: 'Remove address', tone: 'danger' }
    );
    if (!ok) return;
    handleMutation('Address removed.', () => mutations.deleteAddress(address.id)).catch(() => {});
  }, [confirm, handleMutation, mutations]);

  const handleDeleteContact = useCallback(async (contact) => {
    const ok = await confirm(
      `Remove this emergency contact? ${contact.fullName}`,
      { confirmLabel: 'Remove contact', tone: 'danger' }
    );
    if (!ok) return;
    handleMutation('Emergency contact removed.', () => mutations.deleteEmergencyContact(contact.id)).catch(() => {});
  }, [confirm, handleMutation, mutations]);

  const handleBack = () => {
    if (typeof onNavigate === 'function') {
      // W6.2: internal back-navigation teaches the canonical URL.
      // Legacy /employees aliases continue to resolve for bookmarks.
      onNavigate('/workforce', { preserveFilters: false });
      return;
    }
    if (window.history.length > 1) navigate(-1);
    else navigate('/managers');
  };

  const navigateToSurface = useCallback((targetPath) => {
    if (typeof onNavigate === 'function') {
      onNavigate(targetPath, { preserveFilters: false });
      return;
    }
    navigate(targetPath);
  }, [navigate, onNavigate]);

  const launchPersonRequestFlow = useCallback((mode) => {
    const targetPath = mode === 'time-off'
      ? '/requests-calendar#manual-pto-entry'
      : '/requests-calendar#log-attendance-entry';
    const subject = {
      employeeProfileId: loadedProfileId ? String(loadedProfileId) : '',
      managerId: effectiveManagerId ? String(effectiveManagerId) : '',
      subjectLabel: resolveProfileDisplayName(bundle?.profile, managerId)
    };
    try {
      window.sessionStorage.setItem(
        DIRECTOR_ATTENDANCE_FOCUS_STORAGE_KEY,
        JSON.stringify(subject)
      );
    } catch (_err) {
      // Ignore storage failures so navigation still works in locked-down browsers.
    }
    navigateToSurface(targetPath);
  }, [bundle?.profile, effectiveManagerId, loadedProfileId, managerId, navigateToSurface]);

  const handleCreateMissingProfile = useCallback(async () => {
    if (!managerIdValid || !managerId) return;
    const preferredName = [
      managerOverlay?.firstName,
      managerOverlay?.lastName
    ].filter(Boolean).join(' ').trim() || null;
    setIsCreatingMissingProfile(true);
    setCreateMissingProfileError('');
    try {
      await apiRequest('/api/employees/profiles', {
        method: 'POST',
        body: JSON.stringify({
          managerId: Number(managerId),
          userId: null,
          preferredName,
          personalEmail: managerOverlay?.email || null,
          personalPhone: managerOverlay?.mobilePhone || null,
          dateOfHire: managerOverlay?.hireDate || null,
          employmentType: null
        })
      });
      await refetch();
      await onProfileMutated?.();
    } catch (err) {
      setCreateMissingProfileError(err?.message || 'Unable to create employee profile.');
    } finally {
      setIsCreatingMissingProfile(false);
    }
  }, [managerId, managerIdValid, managerOverlay, onProfileMutated, refetch]);

  if (!managerIdValid && !profileIdValid) {
    return (
      <ErrorState
        error={hasProfileSelectionInput ? 'Invalid employee profile id.' : 'Invalid manager id in URL.'}
        onRetry={null}
        onBack={handleBack}
      />
    );
  }

  if (status === 'loading' || status === 'idle') return <LoadingState />;
  // Phase D — surface the manager's name/location in the missing-
  // profile state. managerOverlay is fetched separately from
  // /api/managers/:id so we can show "Alex Angulo (Manager #596 · JFK)"
  // instead of "Manager #596" when an operator lands on a manager
  // that has no employee_profiles row.
  const noProfileManagerName = [managerOverlay?.firstName, managerOverlay?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim() || null;
  const noProfileManagerLocation = managerOverlay?.locationCode || null;

  if (status === 'no-profile' && !usesDirectProfileSelection) {
    return (
      <NoProfileState
        managerId={managerId}
        managerName={noProfileManagerName}
        managerLocationCode={noProfileManagerLocation}
        onBack={handleBack}
        onCreateProfile={handleCreateMissingProfile}
        isCreating={isCreatingMissingProfile}
        error={createMissingProfileError}
        embedded={embedded}
      />
    );
  }

  if (status === 'no-profile' && usesDirectProfileSelection) {
    return (
      <ErrorState
        error="Employee profile not found."
        onRetry={refetch}
        onBack={handleBack}
      />
    );
  }
  if (status === 'error') return <ErrorState error={error} onRetry={refetch} onBack={handleBack} />;

  const { profile, addresses, emergencyContacts, preferences } = bundle || {};
  if (!profile) {
    return (
      <NoProfileState
        managerId={managerId}
        managerName={noProfileManagerName}
        managerLocationCode={noProfileManagerLocation}
        onBack={handleBack}
        onCreateProfile={handleCreateMissingProfile}
        isCreating={isCreatingMissingProfile}
        error={createMissingProfileError}
        embedded={embedded}
      />
    );
  }
  const isArchived = Boolean(profile.archivedAt);
  const isOffboarded = profile.status === 'terminated' && isArchived;
  const profileComplete = Number(profile.profileCompletenessScore || 0) >= 90;
  // Permissions envelope (Commit 8). The route returns a `permissions`
  // block alongside the bundle so the UI can mirror the server gates
  // deterministically. Fallback to all-true for older responses.
  const perms = bundle?.permissions || {
    canRead: true, canReadPII: true, canWrite: true, canWritePII: true,
    canManagePhoto: true, isSelfService: false
  };
  const piiRedacted = bundle?._piiRedacted === true;

  // Sprint 3B.4: when rendered standalone (not embedded), wrap the
  // profile body in WorkspacePage with width="operational" so the
  // Hub tabs + section grids extend to the Sprint 3A 2200px shell.
  // Embedded mode (rendered inside EmployeeDirectoryPage which is
  // ALREADY inside a WorkspacePage) MUST NOT add a second shell —
  // doing so would nest two operational containers and duplicate
  // the page-header rhythm. The inner div keeps its existing
  // data-testid="employee-profile-page" hook so every existing
  // test that asserts on the profile body still passes.
  const profileBody = (
    <div className={embedded ? 'employee-profile employee-profile--embedded' : 'employee-profile'} data-testid="employee-profile-page">
      {!embedded ? (
        <div className="employee-profile__topbar">
          <button type="button" className="employee-profile__back" onClick={handleBack}>← Back</button>
          <span aria-hidden>·</span>
          <span>Employee profile</span>
        </div>
      ) : null}

      {isArchived ? (
        <div className="employee-profile__archived-banner">
          <StatusBanner
            kind="warning"
            message="This profile is archived. Read-only — restore it from the manager roster to enable edits."
          />
        </div>
      ) : null}

      <ProfileHeader
        bundle={bundle}
        managerId={managerId}
        initials={initials}
        photoUrl={photoUrl}
        canManagePhoto={perms.canManagePhoto}
        onUploadPhoto={() => {
          setPhotoUploadOpen(true);
          recordEvent({ eventType: 'modal.open', eventSubtype: 'upload_photo' });
          recordEvent({ eventType: 'drawer.open', eventSubtype: 'upload_photo' });
          recordEvent({ eventType: 'upload.start', eventSubtype: 'photo' });
        }}
      />

      <PersonWorkspaceActionStrip
        canManageAccess
        canWrite={perms.canWrite}
        hasActiveLinkedAccess={Boolean(linkedUserOverlay?.id && linkedUserOverlay?.isActive !== false)}
        isArchived={isArchived}
        onEditProfile={() => openModal('profile')}
        onLogAttendance={() => launchPersonRequestFlow('attendance')}
        onAddPto={() => launchPersonRequestFlow('time-off')}
        onManageAccess={() => navigateToSurface('/users')}
        onDeactivateAccess={handleDeactivateAccess}
      />

      {piiRedacted ? (
        <StatusBanner
          kind="info"
          message="Some personal information is restricted in your view. Ask an admin if you need access."
        />
      ) : null}
      {actionBanner ? <StatusBanner kind="success" message={actionBanner} live /> : null}
      {actionError ? <StatusBanner kind="error" message={actionError} /> : null}

      <ProfileActionRow
        isArchived={isArchived}
        isOffboarded={isOffboarded}
        busy={actionBusy}
        canWrite={perms.canWrite}
        canOffboard={perms.canWrite && !perms.isSelfService}
        onEditProfile={() => openModal('profile')}
        onOffboard={handleOffboard}
        onRestore={handleRestore}
      />

      {!embedded ? <PeopleTalentLinks profile={profile} managerId={managerId} /> : null}

      <EmployeeHubCards
        profile={profile}
        profileComplete={profileComplete}
        isArchived={isArchived}
        perms={perms}
        manager={managerOverlay}
        linkedUser={linkedUserOverlay}
        canonicalUserLink={bundle?.canonicalUserLink || null}
        documents={documentOverlay}
        onCompleteProfile={() => openModal('profile')}
        onUploadDocument={() => {
          setDocumentUploadType('other');
          setDocumentUploadRequest(Date.now());
          handleTabChange('documents');
          recordEvent({ eventType: 'upload.start', eventSubtype: 'document' });
          recordEvent({ eventType: 'modal.open', eventSubtype: 'upload_document' });
          recordEvent({ eventType: 'drawer.open', eventSubtype: 'upload_document' });
        }}
        onAddCertification={() => {
          setDocumentUploadType('certification');
          setDocumentUploadRequest(Date.now());
          handleTabChange('documents');
          recordEvent({ eventType: 'upload.start', eventSubtype: 'certification' });
          recordEvent({ eventType: 'modal.open', eventSubtype: 'upload_document' });
          recordEvent({ eventType: 'drawer.open', eventSubtype: 'upload_document' });
        }}
        onDeactivateAccess={handleDeactivateAccess}
      />

      <EmployeeWorkspaceLayout
        navigation={<HubTabBar activeTab={activeTab} onChange={handleTabChange} tabs={visibleHubTabs} />}
      >
      <div
        className={`employee-hub__panel employee-hub__panel--${activeTab}`}
        role="tabpanel"
        aria-label={visibleHubTabs.find(t => t.id === activeTab)?.label || 'Employee'}
        data-testid={`hub-panel-${activeTab}`}
      >
        {activeTab === 'profile' ? (
          // W2: Profile tab — merged Overview + Employee Profile.
          // Two-column grid preserves every section that used to
          // appear on either tab (identity, employment, contact
          // preview, preferences). The Contact tab keeps the
          // editable lists of addresses + emergency contacts.
          <div data-testid="hub-panel-profile-grid">
            <EmployeeOverviewDashboard
              profile={profile}
              manager={managerOverlay}
              supervisorLabel={currentSupervisorLabel}
              documents={documentOverlay}
              canReadDocuments={perms.canReadDocuments}
              canViewCoaching={canViewCoaching && isModuleEnabled('manager_notes')}
              attendanceEnabled={isModuleEnabled('attendance')}
              summaryContent={(
                <div className="employee-profile__grid">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <IdentitySection
                      profile={profile}
                      canEditProfile={perms.canWrite && !isArchived}
                      onInlineSave={handleInlineProfileSave}
                    />
                    <EmploymentSection
                      profile={profile}
                      supervisorLabel={currentSupervisorLabel}
                      canEditProfile={perms.canWrite && !isArchived}
                      onInlineSave={handleInlineProfileSave}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <ContactSection
                      profile={profile}
                      canEditContact={perms.canWritePII && !isArchived}
                      onInlineSave={handleInlineProfileSave}
                    />
                    <PreferencesPanel
                      preferences={preferences}
                      isArchived={isArchived}
                      canWrite={perms.canWrite}
                      isEditing={isEditingPreferences}
                      onEdit={() => setIsEditingPreferences(true)}
                      onCancelEdit={() => setIsEditingPreferences(false)}
                      onSave={async (payload) => {
                        const result = await handlePreferencesSave(payload);
                        if (result) setIsEditingPreferences(false);
                        return result;
                      }}
                    />
                  </div>
                </div>
              )}
            />
          </div>
        ) : null}

        {activeTab === 'performance' ? (
          <EmployeePerformanceWorkspace
            profile={profile}
            documents={documentOverlay}
            canReadDocuments={perms.canReadDocuments}
            canViewCoaching={canViewCoaching && isModuleEnabled('manager_notes')}
            attendanceEnabled={isModuleEnabled('attendance')}
          />
        ) : null}

        {activeTab === 'goals' ? (
          <EmployeeGoalsWorkspace profile={profile} manager={managerOverlay} />
        ) : null}

        {activeTab === 'organization' ? (
          <EmployeeOrganizationWorkspace
            profile={profile}
            manager={managerOverlay}
            accessibleLocations={accessibleLocations}
            locationsLoading={accessibleLocationsLoading}
          />
        ) : null}

        {activeTab === 'workforce-planning' ? (
          <EmployeeWorkforcePlanningWorkspace
            profile={profile}
            documents={documentOverlay}
            canReadDocuments={perms.canReadDocuments}
            documentsEnabled={isModuleEnabled('documents')}
            accessibleLocations={accessibleLocations}
            locationsLoading={accessibleLocationsLoading}
          />
        ) : null}

        {activeTab === 'career' ? (
          <EmployeeCareerWorkspace
            profile={profile}
            documents={documentOverlay}
            canReadDocuments={perms.canReadDocuments}
            supervisorLabel={currentSupervisorLabel}
            locationLabel={managerOverlay?.locationCode || null}
          />
        ) : null}

        {activeTab === 'attendance' ? (
          <AttendanceTab
            profile={profile}
            effectiveManagerId={effectiveManagerId}
          />
        ) : null}

        {activeTab === 'time-off' ? (
          <TimeOffTab
            profile={profile}
            effectiveManagerId={effectiveManagerId}
          />
        ) : null}

        {activeTab === 'operational-kpis' ? (
          <EmployeeOperationalKpisWorkspace profile={profile} />
        ) : null}

        {activeTab === 'ai-insights' ? (
          <EmployeeAiInsightsWorkspace
            canReadProfile={perms.canRead}
            canViewCoaching={canViewCoaching && isModuleEnabled('manager_notes')}
            canReadDocuments={perms.canReadDocuments}
            attendanceEnabled={isModuleEnabled('attendance')}
            documentsEnabled={isModuleEnabled('documents')}
          />
        ) : null}

        {activeTab === 'contact' ? (
          // Contact Info — addresses, emergency contacts, personal
          // contact, preferences. Single column so each editable
          // list has full width to expand actions.
          <div className="employee-hub__column employee-hub__column--wide">
            <ContactSection
              profile={profile}
              canEditContact={perms.canWritePII && !isArchived}
              onInlineSave={handleInlineProfileSave}
              action={perms.canWritePII ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => openModal('profile')}
                  disabled={isArchived || !perms.canWritePII}
                  data-testid="profile-edit-contact"
                >
                  Edit contact
                </Button>
              ) : null}
            />
            <AddressList
              addresses={addresses}
              isArchived={isArchived}
              canReadPII={perms.canReadPII}
              canWritePII={perms.canWritePII}
              piiRedacted={piiRedacted}
              onAdd={() => { setEditingAddress(null); openModal('address'); }}
              onEdit={(addr) => { setEditingAddress(addr); openModal('address'); }}
              onSetPrimary={handleSetPrimaryAddress}
              onDelete={handleDeleteAddress}
            />
            <EmergencyContactList
              contacts={emergencyContacts}
              isArchived={isArchived}
              canWritePII={perms.canWritePII}
              piiRedacted={piiRedacted}
              onAdd={() => { setEditingContact(null); openModal('contact'); }}
              onEdit={(c) => { setEditingContact(c); openModal('contact'); }}
              onDelete={handleDeleteContact}
            />
            <PreferencesPanel
              preferences={preferences}
              isArchived={isArchived}
              canWrite={perms.canWrite}
              isEditing={isEditingPreferences}
              onEdit={() => setIsEditingPreferences(true)}
              onCancelEdit={() => setIsEditingPreferences(false)}
              onSave={async (payload) => {
                const result = await handlePreferencesSave(payload);
                if (result) setIsEditingPreferences(false);
                return result;
              }}
            />
          </div>
        ) : null}

        {activeTab === 'documents' ? (
          <div className="employee-hub__column employee-hub__column--wide">
            <EmployeeDocumentsPanel
              profileId={profile.id}
              canReadDocuments={perms.canReadDocuments}
              canWriteDocuments={perms.canWriteDocuments}
              defaultDocumentType={documentUploadType}
              uploadRequest={documentUploadRequest}
              onDocumentsChanged={refreshDocumentOverlay}
            />
          </div>
        ) : null}

        {activeTab === 'certifications' ? (
          // W2: Certifications — same EmployeeDocumentsPanel with a
          // documentTypeFilter='certification'. All upload / expiration
          // / alerts behavior is preserved — only the rendered list
          // and the empty-state copy are scoped.
          <div className="employee-hub__column employee-hub__column--wide">
            <EmployeeDocumentsPanel
              profileId={profile.id}
              canReadDocuments={perms.canReadDocuments}
              canWriteDocuments={perms.canWriteDocuments}
              defaultDocumentType="certification"
              documentTypeFilter="certification"
              title="Certifications"
              uploadRequest={documentUploadType === 'certification' ? documentUploadRequest : 0}
              onDocumentsChanged={refreshDocumentOverlay}
            />
          </div>
        ) : null}

        {activeTab === 'coaching' && canViewCoaching ? (
          <div className="employee-hub__column employee-hub__column--wide">
            <EmployeeCoachingTab
              employeeProfileId={profile.id}
              managerId={effectiveManagerId}
              canViewCoaching={canViewCoaching && isModuleEnabled('manager_notes')}
              attendanceEnabled={isModuleEnabled('attendance')}
              managerName={managerOverlay
                ? `${managerOverlay.firstName || ''} ${managerOverlay.lastName || ''}`.trim() || null
                : null}
            />
          </div>
        ) : null}

        {activeTab === 'access' && canViewAccess ? (
          // W5: read-only linked-account view. Edits / resets /
          // deactivation live on the standalone /users page so the
          // Hub stays a lookup surface during the W5 phase.
          <div className="employee-hub__column employee-hub__column--wide">
            <EmployeeAccessTab userId={bundle?.canonicalUserLink?.id || null} />
          </div>
        ) : null}

        {activeTab === 'audit' ? (
          // W2: read-only employee-scoped audit trail. Server filters
          // by entity_type='employee_profile' AND entity_id=:id AND
          // company_id=req.user.companyId — the route gate matches
          // the Hub's existing canReadProfile permission.
          <div className="employee-hub__column employee-hub__column--wide">
            <EmployeeAuditTab profileId={profile.id} />
          </div>
        ) : null}

        {activeTab === 'learning' ? (
          <div className="employee-hub__column employee-hub__column--wide">
            <EmployeeTrainingCertificationsWorkspace
              profileId={profile.id}
              documents={documentOverlay}
              canReadDocuments={perms.canReadDocuments}
              documentsEnabled={isModuleEnabled('documents')}
              onManageCertifications={() => handleTabChange('certifications')}
            />
          </div>
        ) : null}
      </div>
      </EmployeeWorkspaceLayout>

      {activeModal === 'profile' ? (
        <EditEmployeeProfileModal
          open
          onClose={closeAllModals}
          profile={{
            ...profile,
            editLocations: accessibleLocations,
            editSupervisorOptions
          }}
          onSave={handleEditProfileSave}
        />
      ) : null}
      {activeModal === 'address' ? (
        // W4: presentation='drawer' switches the address form to the
        // right-side WorkspaceDrawer. The form body, save handler,
        // and onClose contract are unchanged.
        <AddressModal
          open
          onClose={closeAllModals}
          address={editingAddress}
          onSave={handleAddressSave}
          presentation="drawer"
        />
      ) : null}
      {activeModal === 'contact' ? (
        <EmergencyContactModal
          open
          onClose={closeAllModals}
          contact={editingContact}
          onSave={handleContactSave}
          presentation="drawer"
        />
      ) : null}
      <EmployeePhotoUploadModal
        open={photoUploadOpen}
        onClose={() => setPhotoUploadOpen(false)}
        profileId={profile?.id}
        onUploaded={async () => {
          setActionBanner('Photo uploaded.');
          await refetch();
        }}
        presentation="drawer"
      />
    </div>
  );

  // Sprint 3B.4 wrapper. Embedded mode renders bare — the parent
  // EmployeeDirectoryPage already owns the WorkspacePage shell, so
  // a second wrapper would double-stack the operational container.
  if (embedded) {
    return profileBody;
  }
  return (
    <WorkspacePage
      width="operational"
      data-workspace-id="employee-profile"
      data-testid="employee-profile-workspace"
    >
      {profileBody}
    </WorkspacePage>
  );
}
